import { useState, useEffect, useCallback } from 'react';
import { Layers, Plus, Star, Trash2, Settings2, X, Loader2 } from 'lucide-react';
import { ACCENT, BAD, WARN } from '../lib/uiTokens';
import Dropdown from '../components/receptura/Dropdown';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;
const api = async (url, method = 'GET', body = null) => {
    const opts = { method, headers: { 'X-CSRF-TOKEN': csrf() } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || 'Chyba');
    return r.json();
};

/**
 * Selektor + správa variant AOP plánu (scénářů) pro daný rok.
 * Sedí v kontextovém pruhu Financí (u AOP tabu). Sám drží seznam variant,
 * po změně roku auto-vybere výchozí. Nová varianta = kopie výchozí (backend).
 */
export default function FinanceAopVarianty({ rok, variantaId, setVariantaId, isDark, cardStyle }) {
    const [varianty, setVarianty] = useState([]);
    const [modal, setModal] = useState(false);
    const [novaNazev, setNovaNazev] = useState('');
    const [novaPopis, setNovaPopis] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const load = useCallback(async (autoselect = false) => {
        try {
            const list = await api(`/api/finance/aop/varianty?rok=${rok}`);
            const arr = Array.isArray(list) ? list : [];
            setVarianty(arr);
            if (autoselect || !arr.some(v => v.id === variantaId)) {
                const def = arr.find(v => v.je_vychozi) || arr[0];
                if (def) setVariantaId(def.id);
            }
        } catch (e) { setErr(e.message); }
    }, [rok, variantaId, setVariantaId]);

    // Změna roku → přenačti varianty a vyber výchozí
    useEffect(() => { load(true); /* eslint-disable-next-line */ }, [rok]);

    const vytvor = async () => {
        const nazev = novaNazev.trim();
        if (!nazev) return;
        setBusy(true); setErr('');
        try {
            const nova = await api('/api/finance/aop/varianty', 'POST', { rok, nazev, popis: novaPopis.trim() || null });
            setNovaNazev(''); setNovaPopis('');
            await load();
            setVariantaId(nova.id);
        } catch (e) { setErr(e.message); }
        setBusy(false);
    };

    const prejmenuj = async (id, nazev) => {
        try { await api(`/api/finance/aop/varianty/${id}`, 'PUT', { nazev }); setVarianty(prev => prev.map(v => v.id === id ? { ...v, nazev } : v)); }
        catch (e) { setErr(e.message); }
    };
    const ulozPopis = async (id, popis) => {
        try { await api(`/api/finance/aop/varianty/${id}`, 'PUT', { popis: popis || null }); setVarianty(prev => prev.map(v => v.id === id ? { ...v, popis } : v)); }
        catch (e) { setErr(e.message); }
    };
    const nastavVychozi = async (id) => {
        try { await api(`/api/finance/aop/varianty/${id}`, 'PUT', { je_vychozi: true }); await load(); }
        catch (e) { setErr(e.message); }
    };
    const smaz = async (id) => {
        if (!confirm('Smazat tuto variantu i s jejími daty?')) return;
        try { await api(`/api/finance/aop/varianty/${id}`, 'DELETE'); if (id === variantaId) setVariantaId(null); await load(true); }
        catch (e) { alert(e.message); }
    };

    return (
        <>
            <div className="flex items-center gap-1.5">
                <Layers size={14} style={{ color: ACCENT }} />
                <Dropdown value={variantaId || ''} onChange={v => setVariantaId(Number(v))}
                    options={varianty.length ? varianty.map(v => ({ value: v.id, label: `${v.je_vychozi ? '★ ' : ''}${v.nazev}` })) : [{ value: '', label: '—' }]}
                    isDark={isDark} accent={ACCENT} height={32} minWidth={140} />
                <button onClick={() => setModal(true)} title="Spravovat varianty"
                    className="px-2 py-1.5 rounded-lg" style={{ ...cardStyle, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <Settings2 size={14} />
                </button>
            </div>

            {modal && (
                <div onClick={() => setModal(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ width: 460, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', borderRadius: 12, background: isDark ? 'rgba(24,22,32,0.98)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
                        <div className="flex items-center gap-2 p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <Layers size={18} style={{ color: ACCENT }} />
                            <div className="flex-1 text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Varianty plánu {rok}</div>
                            <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
                        </div>
                        <div className="p-4">
                            <div className="flex gap-2 mb-2">
                                <input value={novaNazev} onChange={e => setNovaNazev(e.target.value)} placeholder="Název (např. Optimistická)"
                                    onKeyDown={e => { if (e.key === 'Enter') vytvor(); }}
                                    className="flex-1 rounded-lg px-3 py-2 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                                <button onClick={vytvor} disabled={busy || !novaNazev.trim()}
                                    className="px-3 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1" style={{ background: ACCENT, opacity: (busy || !novaNazev.trim()) ? 0.5 : 1 }}>
                                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Nová
                                </button>
                            </div>
                            <textarea value={novaPopis} onChange={e => setNovaPopis(e.target.value)} rows={2}
                                placeholder="Popis (volitelně) — co do varianty zapracováváš…"
                                className="w-full rounded-lg px-3 py-2 text-[13px] resize-none mb-2" style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                            <p className="text-[11px] mb-4" style={{ color: 'var(--text-tertiary)' }}>Nová varianta vznikne jako kopie výchozí (★). Pak ji uprav.</p>

                            {varianty.map(v => (
                                <div key={v.id} className="py-2.5" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle, rgba(0,0,0,0.05))'}` }}>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => nastavVychozi(v.id)} title="Nastavit jako výchozí"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: v.je_vychozi ? WARN : 'var(--text-muted)', flexShrink: 0 }}>
                                            <Star size={16} fill={v.je_vychozi ? WARN : 'none'} />
                                        </button>
                                        <input defaultValue={v.nazev}
                                            onBlur={e => { const n = e.target.value.trim(); if (n && n !== v.nazev) prejmenuj(v.id, n); }}
                                            className="flex-1 rounded-md px-2 py-1.5 text-sm font-medium bg-transparent" style={{ color: 'var(--text-primary)', border: '1px solid transparent' }}
                                            onFocus={e => e.currentTarget.style.border = `1px solid ${ACCENT}`}
                                            onBlurCapture={e => e.currentTarget.style.border = '1px solid transparent'} />
                                        <button onClick={() => smaz(v.id)} title="Smazat variantu"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: BAD, flexShrink: 0 }}><Trash2 size={15} /></button>
                                    </div>
                                    <textarea defaultValue={v.popis || ''} rows={2}
                                        onBlur={e => { const p = e.target.value.trim(); if (p !== (v.popis || '')) ulozPopis(v.id, p); }}
                                        placeholder="Popis varianty — co se do ní zapracovává…"
                                        className="w-full mt-1 ml-6 rounded-md px-2 py-1.5 text-[12px] resize-none bg-transparent" style={{ color: 'var(--text-secondary)', border: '1px solid transparent', width: 'calc(100% - 1.5rem)' }}
                                        onFocus={e => e.currentTarget.style.border = `1px solid ${ACCENT}`}
                                        onBlurCapture={e => e.currentTarget.style.border = '1px solid transparent'} />
                                </div>
                            ))}
                            {err && <div className="text-xs mt-3" style={{ color: BAD }}>{err}</div>}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
